export type FeeStructure = {
  /** Service id, used only to break an equal-fee allocation tie deterministically. */
  id?: string;
  estimated?: boolean;
  price?: number; // Item.price (for billing)
  displayPrice?: number; // Service.display_price (for scheduling/display)
  displayPriceMax?: number | null; // Max price for range display (e.g., "$200 - $400")
  priceStartsAt?: boolean; // Show "$200+" instead of fixed price
  originalDisplayPrice?: number | null;
  originalDisplayPriceMax?: number | null;
  promotionName?: string | null;
  bookingFee?: number;
  bookingFeePercentage?: number;
  cancellationFeePctWithin24h?: number;
  cancellationFeePctWithin48h?: number;
  hoursBeforeBookingFeeRefundable?: number | null;
  bookingFeeAutoToCredit?: boolean;
  cancellationCost?: number;
  hoursBeforeCancellingCosts?: number;
  noShowFee?: number | null;
  noShowFeePercentage?: number;
};

export const isPercentageBasedService = (service: FeeStructure): boolean => {
  if (!service) return false;
  const hasPrice = (service.price ?? 0) > 0;
  const hasPctFields = Boolean(
    (service.bookingFeePercentage ?? 0) > 0 ||
    (service.cancellationFeePctWithin24h ?? 0) > 0 ||
    (service.cancellationFeePctWithin48h ?? 0) > 0 ||
    (service.noShowFeePercentage ?? 0) > 0,
  );
  return hasPrice && hasPctFields;
};

/**
 * A percentage of a price, in cents, rounded half-up.
 *
 * Mirrors `round_half_up` in decoda/common/calculate_fee.py -- the helper every
 * money derivation on the backend uses, precisely so the quote shown here and the
 * amount charged agree on .5 boundaries. Keep the operation order (`price * (pct /
 * 100)`) identical to the backend's too: the other grouping takes a different
 * floating-point path and can land either side of a half cent.
 */
const percentOfPriceCents = (price: number, pct: number): number =>
  Math.round(price * (pct / 100));

export const computeBookingFeeCents = (service?: FeeStructure): number => {
  if (!service) return 0;
  if (isPercentageBasedService(service)) {
    return percentOfPriceCents(
      service.price ?? 0,
      service.bookingFeePercentage ?? 0,
    );
  }
  return service.bookingFee ?? 0;
};

/**
 * How a multi-service appointment's booking fee is derived. Mirrors the backend
 * BookingFeeMode enum (decoda/enums/booking_fee_mode.py).
 */
export type BookingFeeMode = "PER_SERVICE" | "HIGHEST_SERVICE";

/**
 * Split an appointment's booking fee across its services, one amount per entry
 * positionally aligned with the input. Mirrors allocate_booking_fees() in
 * decoda/common/booking_fee.py -- keep the two in step.
 *
 * Under HIGHEST_SERVICE the appointment owes one fee no matter how many
 * services are stacked on it, so the full amount is attributed to the service
 * that set it and the rest are zeroed. Ties resolve toward the service that
 * converts its fee to patient credit (so an equal-fee tie never costs the
 * patient their deposit credit), then toward the larger service id -- the same
 * order as the backend's `(fee, auto_to_credit, id)` tuple, so the quote the
 * patient is shown names the service the charge will actually bill.
 */
export const allocateBookingFeeCents = (
  services: Array<FeeStructure | null | undefined>,
  mode: BookingFeeMode = "PER_SERVICE",
): number[] => {
  const fees = services.map((service) =>
    computeBookingFeeCents(service ?? undefined),
  );
  if (mode === "PER_SERVICE" || fees.length === 0) return fees;

  const beats = (i: number, current: number): boolean => {
    if (fees[i] !== fees[current]) return fees[i] > fees[current];
    const credits = Boolean(services[i]?.bookingFeeAutoToCredit);
    const currentCredits = Boolean(services[current]?.bookingFeeAutoToCredit);
    if (credits !== currentCredits) return credits;
    return (services[i]?.id ?? "") > (services[current]?.id ?? "");
  };

  let winner = 0;
  for (let i = 1; i < fees.length; i += 1) {
    if (beats(i, winner)) winner = i;
  }
  return fees.map((fee, i) => (i === winner ? fee : 0));
};

/** What the appointment owes up front under `mode`. */
export const computeTotalBookingFeeCents = (
  services: Array<FeeStructure | null | undefined>,
  mode: BookingFeeMode = "PER_SERVICE",
): number =>
  allocateBookingFeeCents(services, mode).reduce(
    (total, fee) => total + fee,
    0,
  );

/**
 * How much of the upfront amount comes back as patient credit. Keyed off the
 * ALLOCATED fee rather than each service's configured fee: under
 * HIGHEST_SERVICE only one service is actually charged, so summing configured
 * fees here would promise the patient credit they never paid for.
 */
export const computeBookingFeeCreditCents = (
  services: Array<FeeStructure | null | undefined>,
  mode: BookingFeeMode = "PER_SERVICE",
): number =>
  allocateBookingFeeCents(services, mode).reduce(
    (total, fee, i) => total + (services[i]?.bookingFeeAutoToCredit ? fee : 0),
    0,
  );

export const computeNoShowFeeCents = (service?: FeeStructure): number => {
  if (!service) return 0;
  if (isPercentageBasedService(service)) {
    return percentOfPriceCents(
      service.price ?? 0,
      service.noShowFeePercentage ?? 0,
    );
  }
  return service.noShowFee ?? 0;
};

/**
 * Format cancellation fee information for display
 * Returns an array of formatted strings describing cancellation fees
 * Note: This function should be used with formatCurrency from format-data for currency formatting
 */
/**
 * Format the display price based on the pricing mode
 * Returns formatted string based on the mode:
 * - Range: "$200 - $400"
 * - Starts at: "$200+"
 * - Price varies: "$200 (price varies)"
 * - Fixed: "$200"
 */
export const formatDisplayPrice = (
  pricing: FeeStructure | undefined,
  formatCurrencyFn: (amount: number) => string,
): string | null => {
  if (
    !pricing ||
    (pricing.displayPrice == null && pricing.displayPriceMax == null)
  ) {
    return null;
  }

  const minPrice = formatCurrencyFn(pricing.displayPrice ?? 0);

  // Range mode: show "$200 - $400"
  if (
    pricing.displayPriceMax != null &&
    pricing.displayPriceMax > (pricing.displayPrice ?? 0)
  ) {
    const maxPrice = formatCurrencyFn(pricing.displayPriceMax);
    return `${minPrice} - ${maxPrice}`;
  }

  // Starts at mode: show "$200+"
  if (pricing.priceStartsAt) {
    return `${minPrice}+`;
  }

  // Price varies mode: show "$200*"
  if (pricing.estimated) {
    return `${minPrice}*`;
  }

  // Fixed price mode: show "$200"
  return minPrice;
};

/** Format the item's billing price for staff-facing scheduling flows. */
export const formatItemPrice = (
  pricing: FeeStructure | undefined,
  formatCurrencyFn: (amount: number) => string,
): string | null =>
  pricing?.price == null ? null : formatCurrencyFn(pricing.price);

/**
 * Compute the total item price for multiple services in staff-facing
 * scheduling flows. A missing item price suppresses the total rather than
 * showing a partial amount.
 */
export const computeAppointmentTotal = (
  services: Array<FeeStructure | null | undefined>,
  formatCurrencyFn: (amount: number) => string,
): string | null => {
  if (
    services.length === 0 ||
    services.some((service) => service?.price == null)
  ) {
    return null;
  }

  return formatCurrencyFn(
    services.reduce((total, service) => total + (service?.price ?? 0), 0),
  );
};

export const getAppointmentTotalLabel = (): string => "Appointment Total";

export const getAppointmentTotalDisplay = (
  services: Array<FeeStructure | null | undefined>,
  formatCurrencyFn: (amount: number) => string,
): { label: string; total: string } | null => {
  const total = computeAppointmentTotal(services, formatCurrencyFn);
  if (!total) return null;

  return {
    label: getAppointmentTotalLabel(),
    total,
  };
};

export const getCancellationFeeInfo = (
  service: FeeStructure,
): Array<{ text: string; amountCents?: number }> => {
  if (!service) return [];

  // Check if service has any cancellation fees
  const hasPercentageFees =
    (service.cancellationFeePctWithin24h ?? 0) > 0 ||
    (service.cancellationFeePctWithin48h ?? 0) > 0;
  const hasFixedFee =
    (service.cancellationCost ?? 0) > 0 &&
    (service.hoursBeforeCancellingCosts ?? 0) > 0;

  if (!hasPercentageFees && !hasFixedFee) return [];

  const fees: Array<{ text: string; amountCents?: number }> = [];
  const price = service.price ?? 0;

  // Percentages take precedence over fixed cancellation fee
  if (hasPercentageFees) {
    // 48h cancellation fee
    if ((service.cancellationFeePctWithin48h ?? 0) > 0) {
      const pct48 = service.cancellationFeePctWithin48h ?? 0;
      const amount48 = Math.round(price * (pct48 / 100));
      fees.push({
        text: `If you cancel before 48 hours of the appointment's start time.`,
        amountCents: price > 0 ? amount48 : undefined,
      });
    }

    // 24h cancellation fee
    if ((service.cancellationFeePctWithin24h ?? 0) > 0) {
      const pct24 = service.cancellationFeePctWithin24h ?? 0;
      const amount24 = Math.round(price * (pct24 / 100));
      fees.push({
        text: `If you cancel within 24 hours of the appointment's start time.`,
        amountCents: price > 0 ? amount24 : undefined,
      });
    }
  } else {
    // Fixed cancellation fee (only shown if no percentages)
    const cancellationCost = service.cancellationCost ?? 0;
    const hoursBefore = service.hoursBeforeCancellingCosts ?? 0;
    if (cancellationCost > 0 && hoursBefore > 0) {
      fees.push({
        text: `If you cancel within ${hoursBefore} hours of the appointment's start time.`,
        amountCents: cancellationCost,
      });
    }
  }

  return fees;
};
